import {
  AdminSpinner,
  AdvancedQueryTypes,
  AllProvidersModal,
  AllProvidersModalComponent,
  ChartsFilter,
  ComparedOrdersOverviewEntity,
  ComparedPeriodType,
  Div,
  getDashboardComparedOverview,
  GetDashboardComparedOverviewParams,
  getDashboardOrdersToday,
  getDashboardOverview,
  GetDashboardOverviewParams,
  GetDashboardTodayParams,
  getTimeZone,
  GridCharts,
  GroupByType,
  OrdersCharts,
  OrdersOverviewEntity,
  OrdersTodayInfoEntity,
  PeriodType,
  SummaryComponent,
  SummaryFilter,
  reportError,
} from '@vgrubs/components';
import { isAxiosError } from 'axios';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { summary } from '../../dummyData';
import { LifetimePerformance } from './LifetimePerformance';
import { UrgentProblemsBar } from '../../../SystemProblems';

function getGroupByType(fromISO: string, toISO: string): GroupByType {
  const fromDate = DateTime.fromISO(fromISO);
  const toDate = DateTime.fromISO(toISO);

  const diffInDays = toDate.diff(fromDate, 'days').days;
  const diffInMonths = toDate.diff(fromDate, 'months').months;
  // const diffInYears = toDate.diff(fromDate, 'years').years;

  if (diffInDays < 1) {
    return GroupByType.HOUR;
  } else if (diffInDays <= 31) {
    return GroupByType.DAY;
  } else if (diffInMonths <= 12) {
    return GroupByType.MONTH;
  } else {
    return GroupByType.YEAR;
  }
}

export const OrdersSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [advancedQueryTypes] = useState<AdvancedQueryTypes[]>(
    Object.values(AdvancedQueryTypes) as AdvancedQueryTypes[]
  );
  const [todayDashboard, setTodayDashboard] =
    useState<OrdersTodayInfoEntity | null>(null);
  const type = searchParams.get('type') || advancedQueryTypes[0];
  const [overview, setOverview] = useState<OrdersOverviewEntity | null>(null);

  const [overviewComparedTo, setOverviewComparedTo] =
    useState<ComparedOrdersOverviewEntity | null>(null);

  const from =
    searchParams.get('from') || DateTime.now().startOf('month').toISODate();
  const to =
    searchParams.get('to') || DateTime.now().startOf('day').toISODate();

  const compareToFrom =
    searchParams.get('compareToFrom') ||
    DateTime.now().startOf('month').toISODate();

  const compareToTo =
    searchParams.get('compareToTo') ||
    DateTime.now().startOf('day').toISODate();

  const selectedLocationId = searchParams.get('locationId');
  const selectedBrandId = searchParams.get('brandId');
  const brandTemplateId = searchParams.get('brandTemplateId');

  const periodTypeComparedTo = searchParams.get('periodTypeComparedTo') || '';

  const [allProvidersModal, setAllProvidersModal] =
    useState<AllProvidersModal | null>(null);

  const groupByType = getGroupByType(from, to);

  async function getOverview(groupByType: GroupByType) {
    try {
      setLoadingOverview(true);

      const params: GetDashboardOverviewParams = {
        type,
        periodType: PeriodType.CUSTOM,
        groupByType,
        from: DateTime.fromISO(from).startOf('day').toISODate() || '',
        to: DateTime.fromISO(to).endOf('day').toISODate() || '',
        timeZone: getTimeZone,
      };

      if (
        type === AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_ANY_BRAND ||
        type === AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_V_GRUBS ||
        type === AdvancedQueryTypes.LOCATION_SPECIFIC_WITH_V_GRUBS ||
        type === AdvancedQueryTypes.LOCATION_SPECIFIC_ONLY_V_GRUBS
      ) {
        params.locationIds = [selectedLocationId!];
      }
      if (type === AdvancedQueryTypes.V_GRUBS_BRAND_SPECIFIC) {
        params.brandTemplateIds = [brandTemplateId!];
      }
      if (type === AdvancedQueryTypes.LOCATION_BRAND_SPECIFIC) {
        params.locationIds = [selectedLocationId!];
        if (selectedBrandId) {
          params.brandIds = [selectedBrandId!];
        }
      }

      const { data: overview } = await getDashboardOverview(params);

      setOverview(overview.data);
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.data.message !== 'Unauthorized'
      ) {
        reportError(error);
      }
    } finally {
      setLoadingOverview(false);
    }
  }

  async function getDashboard() {
    try {
      setLoading(true);

      const params: GetDashboardTodayParams = {
        type,
        timeZone: getTimeZone,
      };

      if (
        type === AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_ANY_BRAND ||
        type === AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_V_GRUBS ||
        type === AdvancedQueryTypes.LOCATION_SPECIFIC_WITH_V_GRUBS ||
        type === AdvancedQueryTypes.LOCATION_SPECIFIC_ONLY_V_GRUBS
      ) {
        params.locationIds = [selectedLocationId!];
      }
      if (type === AdvancedQueryTypes.V_GRUBS_BRAND_SPECIFIC) {
        params.brandTemplateIds = [brandTemplateId!];
      }
      if (type === AdvancedQueryTypes.LOCATION_BRAND_SPECIFIC) {
        params.locationIds = [selectedLocationId!];
        if (selectedBrandId) {
          params.brandIds = [selectedBrandId!];
        }
      }

      const { data: ordersToday } = await getDashboardOrdersToday(params);
      setTodayDashboard(ordersToday.data);
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.data.message !== 'Unauthorized'
      ) {
        reportError(error);
      }
    } finally {
      setLoading(false);
    }
  }

  async function getOverviewComparedTo(
    groupByType: GroupByType,
    periodType: ComparedPeriodType
  ) {
    try {
      setLoadingOverview(true);

      const params: GetDashboardComparedOverviewParams = {
        from: DateTime.fromISO(from).startOf('day').toISODate() || '',
        to: DateTime.fromISO(to).endOf('day').toISODate() || '',
        compareToFrom:
          periodType === ComparedPeriodType.CUSTOM
            ? DateTime.fromISO(compareToFrom).startOf('day').toISODate() || ''
            : undefined,
        compareToTo:
          periodType === ComparedPeriodType.CUSTOM
            ? DateTime.fromISO(compareToTo).endOf('day').toISODate() || ''
            : undefined,
        type,
        compareToType: periodType,
        groupByType:
          periodType === ComparedPeriodType.CUSTOM
            ? GroupByType.DAY
            : groupByType,
        timeZone: getTimeZone,
      };

      if (
        type === AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_ANY_BRAND ||
        type === AdvancedQueryTypes.LOCATION_SPECIFIC_WITHOUT_V_GRUBS ||
        type === AdvancedQueryTypes.LOCATION_SPECIFIC_WITH_V_GRUBS ||
        type === AdvancedQueryTypes.LOCATION_SPECIFIC_ONLY_V_GRUBS
      ) {
        params.locationIds = [selectedLocationId!];
      }
      if (type === AdvancedQueryTypes.V_GRUBS_BRAND_SPECIFIC) {
        params.brandTemplateIds = [brandTemplateId!];
      }
      if (type === AdvancedQueryTypes.LOCATION_BRAND_SPECIFIC) {
        params.locationIds = [selectedLocationId!];
        if (selectedBrandId) {
          params.brandIds = [selectedBrandId!];
        }
      }

      const { data: overview } = await getDashboardComparedOverview(params);

      setOverviewComparedTo(overview.data as any);
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.data.message !== 'Unauthorized'
      ) {
        reportError(error);
      }
    } finally {
      setLoadingOverview(false);
    }
  }

  useEffect(() => {
    if (advancedQueryTypes.length === 0) return;
    getDashboard();
  }, [
    advancedQueryTypes,
    type,
    selectedLocationId,
    selectedBrandId,
    brandTemplateId,
  ]);

  useEffect(() => {
    if (advancedQueryTypes.length === 0) return;
    if (periodTypeComparedTo !== '') {
      getOverviewComparedTo(
        groupByType,
        periodTypeComparedTo as ComparedPeriodType
      );
    } else {
      getOverview(groupByType);
      // setOverviewComparedTo(null);
    }
  }, [
    advancedQueryTypes,
    type,
    from,
    to,
    compareToFrom,
    compareToTo,
    periodTypeComparedTo,
    selectedLocationId,
    selectedBrandId,
    brandTemplateId,
  ]);

  return (
    <>
      {/* First thing on the tab, and only when something critical is open;
          the full picture is the System Problems page, which a click opens. */}
      <UrgentProblemsBar />

      <SummaryFilter
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        advancedQueryTypes={advancedQueryTypes}
      />

      {advancedQueryTypes.length > 0 && (
        <Div
          $flex
          $gap="24px"
          $gapSM="16px"
          $m="32px 0"
          $mSM="20px 0"
          $columnSM
          $relative
          $tabletUp
        >
          {loading && (
            <AdminSpinner small absolute overflowBorderRadius="20px" creme />
          )}
          {summary(todayDashboard).map(
            ({ amount, color, label, Icon, byProvider, currency }, i) => {
              return (
                <SummaryComponent
                  amount={amount}
                  color={color}
                  label={label}
                  Icon={Icon}
                  key={i}
                  byProvider={byProvider || []}
                  setAllProvidersModal={setAllProvidersModal}
                  currency={currency}
                />
              );
            }
          )}
        </Div>
      )}
      {advancedQueryTypes.length > 0 && (
        <Div $tablet>
          {loading && (
            <AdminSpinner small absolute overflowBorderRadius="20px" creme />
          )}
          <GridCharts $width="30%" $widthMD="30%">
            {summary(todayDashboard).map(
              ({ amount, color, label, Icon, byProvider, currency }, i) => {
                return (
                  <SummaryComponent
                    amount={amount}
                    color={color}
                    label={label}
                    Icon={Icon}
                    key={i}
                    byProvider={byProvider || []}
                    setAllProvidersModal={setAllProvidersModal}
                    currency={currency}
                  />
                );
              }
            )}
          </GridCharts>
        </Div>
      )}

      <Div>
        <ChartsFilter
          from={from}
          to={to}
          setSearchParams={setSearchParams}
          compareToFrom={compareToFrom || ''}
          compareToTo={compareToTo || ''}
          comparedTo={periodTypeComparedTo}
        />
        <OrdersCharts
          data={overview}
          loading={loadingOverview}
          setAllProvidersModal={setAllProvidersModal}
          groupByType={
            periodTypeComparedTo === ComparedPeriodType.CUSTOM
              ? GroupByType.DAY
              : groupByType
          }
          periodTypeComparedTo={
            periodTypeComparedTo as ComparedPeriodType | undefined
          }
          comparedData={overviewComparedTo}
        />
      </Div>

      <LifetimePerformance />

      <AllProvidersModalComponent
        allProvidersModal={allProvidersModal}
        setAllProvidersModal={setAllProvidersModal}
      />
    </>
  );
};
